import React, { useMemo, useState } from "react"
import dayjs, { Dayjs } from "dayjs"
import { Segmented, DatePicker, Alert, Select, Empty, Spin } from "antd"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Button } from "components"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import usePermissions from "hooks/usePermissions"
import { useAppNotificationStore } from "stores"
import { onQueryError } from "utils/utils"
import SegmentWrapper from "components/custom/SegmentWrapper"
import FeatureHint from "components/new-stuff/ui/FeatureHint"
import { LeaveTimeline } from "components/new-stuff/leave-timeline"
import LeaveMonthGrid from "components/new-stuff/leave-timeline/LeaveMonthGrid"
import type {
  LeaveTimelineBar,
  LeaveTimelineDayFlag,
  LeaveTimelineRow,
} from "components/new-stuff/leave-timeline"
import {
  ILeaveDepartmentOption,
  leaveService,
  LEAVE_ENDPOINTS,
  ILeavePlanningCycle,
  ILeavePlanningPeriod,
  PLANNING_STATUS,
} from "services/leaveService"
import RosterPeriodDrawer from "./RosterPeriodDrawer"
import {
  LeavePlanFlowLink,
  LeavePlanStatusTag,
  planStatusLabel,
} from "./leavePlanStatus"
import OpenPlanningCycleModal from "./OpenPlanningCycleModal"

const ISO = "YYYY-MM-DD"

/** Stable identity so the anchor memo does not re-run on every render before data lands. */
const EMPTY_PERIODS: ILeavePlanningPeriod[] = []

type RangeMode = "month" | "quarter" | "timeline"

/** DD/MM/YYYY from the API, as a date. Split rather than parsed, as elsewhere here. */
const fromApiDate = (value?: string | null) => {
  if (!value) return null
  const [day, month, year] = value.split("/")
  const date = dayjs(`${year}-${month}-${day}`, ISO)
  return date.isValid() ? date : null
}

const hasOpened = (cycle: ILeavePlanningCycle) => {
  const opens = fromApiDate(cycle.opens_on)
  return !opens || !opens.isAfter(dayjs(), "day")
}

/**
 * Which cycle to show first: the one employees are planning into now, then one scheduled to
 * open, then the most recent closed one. The list arrives newest year first.
 */
const pickDefaultCycle = (cycles: ILeavePlanningCycle[]) =>
  cycles.find((c) => !c.is_closed && hasOpened(c)) ??
  cycles.find((c) => !c.is_closed) ??
  cycles[0] ??
  null

const cycleStatus = (cycle: ILeavePlanningCycle) => {
  if (cycle.is_closed) return `closed ${cycle.closed_on ?? ""}`.trim()
  if (!hasOpened(cycle)) return `opens ${cycle.opens_on}`
  return cycle.submission_deadline
    ? `entries close ${cycle.submission_deadline}`
    : "open"
}

/**
 * AL-05/06. Every tentative period on a department's roster, so a supervisor can coordinate
 * the year in one view instead of reconciling requests one at a time.
 *
 * Drafts never arrive here — the server excludes them, because a period an employee has not
 * submitted is theirs alone. What is shown is an intention, not a booking: nothing on this
 * screen has moved a balance, which is why days are labelled indicative throughout.
 */
const LeavePlanningRoster: React.FC = () => {
  // Empty means every department the viewer may see; the server narrows it otherwise.
  const [departmentIds, setDepartmentIds] = useState<number[]>([])
  const [rangeMode, setRangeMode] = useState<RangeMode>("quarter")
  const [anchor, setAnchor] = useState<Dayjs | null>(null)
  const [selected, setSelected] = useState<ILeavePlanningPeriod | null>(null)
  const [selectedCycleId, setSelectedCycleId] = useState<number | null>(null)
  const [openModal, setOpenModal] = useState(false)
  const [confirmClose, setConfirmClose] = useState(false)
  const queryClient = useQueryClient()
  const { toast } = useAppNotificationStore()
  const [canManageCycles] = usePermissions({
    allowedPermissions: ["can_edit_leave_policies"],
  })

  // Every cycle, not only the open one: a cycle set up to open later has to be visible to
  // the HR who scheduled it, and a closed cycle's roster is still worth looking back at.
  const { data: cyclesRes, isLoading: cycleLoading } = useQuery({
    queryKey: [LEAVE_ENDPOINTS.VIEW_PLANNING_CYCLES],
    queryFn: () => leaveService.getPlanningCycles(),
    staleTime: 0,
  })

  const cycles = useMemo(() => cyclesRes?.data ?? [], [cyclesRes])
  const cycle =
    cycles.find((c) => c.leave_planning_cycle_id === selectedCycleId) ??
    pickDefaultCycle(cycles)
  const cycleId = cycle?.leave_planning_cycle_id

  const refreshCycles = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: [LEAVE_ENDPOINTS.VIEW_PLANNING_CYCLES],
      }),
      queryClient.invalidateQueries({
        queryKey: [LEAVE_ENDPOINTS.VIEW_PLANNING_CYCLE],
      }),
    ])

  const closeCycle = useMutation({
    mutationFn: (id: number) => leaveService.closePlanningCycle(id),
    onSuccess: async (res: any) => {
      toast.success(res?.message || "Leave planning is closed.")
      await refreshCycles()
    },
    onError: onQueryError,
  })

  const openCycleModal = (
    <OpenPlanningCycleModal
      open={openModal}
      takenYears={cycles.map((c) => c.planning_year)}
      onClose={() => setOpenModal(false)}
      onOpened={async () => {
        setOpenModal(false)
        setSelectedCycleId(null)
        await refreshCycles()
      }}
    />
  )

  const { data: departmentsRes } = useQuery({
    queryKey: [LEAVE_ENDPOINTS.VIEW_LEAVE_DEPARTMENTS],
    queryFn: () => leaveService.getDepartmentOptions(),
  })

  const departments: ILeaveDepartmentOption[] =
    (departmentsRes as { data?: ILeaveDepartmentOption[] } | undefined)?.data ?? []

  const {
    data: rosterRes,
    isLoading: rosterLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: [LEAVE_ENDPOINTS.VIEW_PLANNING_ROSTER, cycleId, departmentIds],
    // The roster is the whole cycle in one call; the range control only pans the window we
    // draw. Refetching per quarter would triple the round trips to redraw data we hold.
    queryFn: () =>
      leaveService.getPlanningRoster(cycleId as number, departmentIds),
    enabled: !!cycleId,
    staleTime: 0,
  })

  const roster = rosterRes?.data
  const periods = useMemo(() => roster?.periods ?? EMPTY_PERIODS, [roster])

  // TO-06. The server marks the days where too many people in a department have planned to be
  // away, counted against the same threshold the approval screen uses. Only breaching days
  // come back, so this maps straight through without re-deriving anything.
  const dayFlags = useMemo<LeaveTimelineDayFlag[]>(
    () =>
      (roster?.days ?? []).reduce<LeaveTimelineDayFlag[]>((acc, day) => {
        const date = toIso(day.date)
        if (date) {
          acc.push({
            date,
            exceedsThreshold: day.exceeds_threshold,
            absentCount: day.absent_count,
          })
        }
        return acc
      }, []),
    [roster]
  )

  // The grid needs the same clash days the timeline shades, keyed for lookup per cell.
  const flaggedDays = useMemo<Record<string, number>>(
    () =>
      dayFlags.reduce<Record<string, number>>((acc, flag) => {
        if (flag.exceedsThreshold) acc[flag.date] = flag.absentCount ?? 0
        return acc
      }, {}),
    [dayFlags]
  )

  // Planned periods drawn as calendar entries. Everything on this roster is an intention, so
  // they all carry the "planned" tone rather than being coloured by status.
  const calendarEntries = useMemo(
    () =>
      periods.map((period) => ({
        employee_request_id: period.leave_planning_period_id,
        person_id: period.person_id,
        full_name: period.full_name,
        department_id: period.department_id,
        department: period.department,
        first_day: period.first_day,
        last_day: period.last_day,
        kind: "planned" as const,
        leave_policy: period.leave_policy,
        label: period.leave_policy,
      })),
    [periods]
  )

  /**
   * Where to open the view.
   *
   * Not today: planning is done months ahead, so the current quarter is usually empty and
   * the supervisor lands on a screen that looks broken. The earliest period still to come
   * is where the coordination actually is — falling back to today only when the roster is
   * empty and there is nothing to point at.
   */
  const effectiveAnchor = useMemo(() => {
    if (anchor) return anchor

    const today = dayjs()
    const upcoming = periods
      .map((p) => toIso(p.first_day))
      .filter((iso): iso is string => !!iso)
      .map((iso) => dayjs(iso))
      .filter((d) => !d.isBefore(today, "day"))
      .sort((a, b) => a.valueOf() - b.valueOf())

    if (upcoming.length > 0) return upcoming[0]
    if (!cycle) return today

    return today.year() === cycle.planning_year
      ? today
      : today.year(cycle.planning_year).startOf("year")
  }, [anchor, cycle, periods])

  const { start, end } = useMemo(() => {
    if (rangeMode === "month") {
      return {
        start: effectiveAnchor.startOf("month"),
        end: effectiveAnchor.endOf("month"),
      }
    }

    // Computed rather than startOf("quarter"), which needs dayjs's quarterOfYear plugin --
    // not extended globally, so relying on it would work or not depending on load order.
    const quarterStart = effectiveAnchor
      .month(Math.floor(effectiveAnchor.month() / 3) * 3)
      .startOf("month")
    return {
      start: quarterStart,
      end: quarterStart.add(2, "month").endOf("month"),
    }
  }, [effectiveAnchor, rangeMode])

  // Rows are limited to people with something in the window: a department of sixty listed
  // against an empty quarter is a wall of blank rows to scroll past, and it reads as though
  // the data failed to load.
  const { rows, bars, byId } = useMemo(
    () => buildRoster(periods, start.format(ISO), end.format(ISO)),
    [periods, start, end]
  )

  // Only stated for a single department. Across several there is no one limit, and the server
  // returns none rather than pick one.
  const thresholdLabel = roster?.threshold_configured
    ? roster.threshold === 1
      ? "Only one person may be away at once in this department."
      : `Up to ${roster.threshold} people may be away at once in this department.`
    : undefined

  if (cycleLoading) {
    return (
      <SegmentWrapper title="Leave planning roster">
        <div className="flex justify-center py-16">
          <Spin />
        </div>
      </SegmentWrapper>
    )
  }

  if (!cycle) {
    return (
      <SegmentWrapper title="Leave planning roster">
        <div className="px-4 md:px-6 lg:px-8 xl:px-[60px] pb-10">
          <Empty
            description={
              <span className="font-avenir text-padeSubtext">
                {canManageCycles
                  ? "Leave planning has not been opened yet. Open it for a year and employees can start adding the leave they intend to take."
                  : "Leave planning has not been opened yet. Once HR opens it for the year, the periods people enter will appear here."}
              </span>
            }
          >
            {canManageCycles ? (
              <Button
                color="primary"
                onClick={() => setOpenModal(true)}
              >
                Open leave planning
              </Button>
            ) : null}
          </Empty>
        </div>
        {openCycleModal}
      </SegmentWrapper>
    )
  }

  return (
    <SegmentWrapper
      title={
        <span className="flex items-center gap-2">
          Leave planning roster
          <FeatureHint>
            What your department intends to take next year. Nothing here is booked
            and no balance has moved. Days shaded red are where more people plan
            to be away than the department allows at once — counted per person,
            not per period.
          </FeatureHint>
        </span>
      }
      // The planning year and what can be done to it belong with the heading: they set what the
      // whole roster is about. The controls below only change how it is looked at.
      subtitle={
        <span className="font-avenir text-sm text-padeSubtext">
          {cycle.planning_year} cycle · {cycleStatus(cycle)} · Click a person to
          agree their dates or ask them to move
        </span>
      }
      actionBtn={
        <div className="flex flex-wrap items-center justify-end gap-3">
          {cycles.length > 1 ? (
            <Select
              aria-label="Planning year"
              value={cycle.leave_planning_cycle_id}
              onChange={(id) => {
                setSelectedCycleId(id)
                setAnchor(null)
              }}
              className="w-[110px]"
              options={cycles.map((c) => ({
                label: `${c.planning_year}`,
                value: c.leave_planning_cycle_id,
              }))}
            />
          ) : null}
          {canManageCycles && !cycle.is_closed ? (
            <Button
              color="neutral"
              onClick={() => setConfirmClose(true)}
            >
              Close planning
            </Button>
          ) : null}
          {canManageCycles ? (
            <Button
              color="secondary"
              onClick={() => setOpenModal(true)}
            >
              Open another year
            </Button>
          ) : null}
        </div>
      }
    >
      <div className="px-4 md:px-6 lg:px-8 xl:px-[60px] pb-6 flex flex-col gap-5">
        {/* One toolbar: who is shown on the left, how the period is viewed on the right. */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Select
            mode="multiple"
            aria-label="Departments"
            value={departmentIds}
            onChange={setDepartmentIds}
            maxTagCount="responsive"
            allowClear
            showSearch
            // Companies here run to hundreds of departments, so scrolling to one is not a
            // realistic way to find it.
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            placeholder="All departments"
            className="w-full sm:w-[320px]"
            options={departments.map((d) => ({
              label: d.description,
              value: d.departmentId,
            }))}
          />

          <div className="flex flex-wrap items-center gap-3">
            <Segmented
              value={rangeMode}
              onChange={(v) => setRangeMode(v as RangeMode)}
              options={[
                { label: "Month", value: "month" },
                { label: "Quarter", value: "quarter" },
                { label: "Timeline", value: "timeline" },
              ]}
            />
            <div className="flex flex-col">
              <DatePicker
                aria-label="Period"
                picker={rangeMode === "month" ? "month" : "quarter"}
                value={effectiveAnchor}
                onChange={(v) => v && setAnchor(v)}
                allowClear={false}
                className="w-[160px]"
              />
            </div>
            <span className="font-avenir text-xs text-padeSubtext whitespace-nowrap tabular-nums">
              {start.format("DD MMM")} – {end.format("DD MMM YYYY")}
            </span>
          </div>
        </div>

        {isError ? (
          <Alert
            type="warning"
            showIcon
            message="Could not load the roster"
            description="Try again, or reload the page."
          />
        ) : null}

        {/* What the roster shows and the states on it, as one note rather than a banner with a
            loose row of chips under it. Each status explains itself on hover. */}
        <Alert
          type="info"
          showIcon
          message="These are intentions, not bookings"
          description={
            <div className="flex flex-col gap-3">
              <span>
                Nothing here has been charged against a balance. Days shown are
                indicative until the employee requests the leave.
              </span>
              <div className="flex flex-wrap items-center gap-2">
                {[
                  PLANNING_STATUS.SUBMITTED,
                  PLANNING_STATUS.REVISION_REQUESTED,
                  PLANNING_STATUS.COORDINATED,
                  PLANNING_STATUS.CONVERTED,
                ].map((statusId) => (
                  <LeavePlanStatusTag
                    key={statusId}
                    statusId={statusId}
                    audience="supervisor"
                  />
                ))}
                <span className="ml-1">
                  <LeavePlanFlowLink audience="supervisor" />
                </span>
              </div>
            </div>
          }
        />

        {rangeMode === "timeline" ? (
          <LeaveTimeline
            rows={rows}
            bars={bars}
            dayFlags={dayFlags}
            startDate={start.format(ISO)}
            endDate={end.format(ISO)}
            loading={rosterLoading}
            emptyMessage="Nobody has planned leave in this period."
            thresholdLabel={thresholdLabel}
            onBarClick={(bar) =>
              setSelected(byId.get(bar.id as number) ?? null)
            }
          />
        ) : rangeMode === "month" ? (
          <LeaveMonthGrid
            month={effectiveAnchor}
            entries={calendarEntries}
            days={[]}
            flaggedDays={flaggedDays}
            loading={rosterLoading}
            emptyMessage="Nobody has planned leave this month."
            onEntryClick={(entry) =>
              setSelected(byId.get(entry.employee_request_id as number) ?? null)
            }
          />
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            {[0, 1, 2].map((offset) => {
              const monthAnchor = start.add(offset, "month")
              return (
                <div
                  key={monthAnchor.format("YYYY-MM")}
                  className="flex flex-col gap-2 min-w-0"
                >
                  <h4 className="font-circular text-sm font-semibold text-padeBlack m-0">
                    {monthAnchor.format("MMMM YYYY")}
                  </h4>
                  <LeaveMonthGrid
                    month={monthAnchor}
                    entries={calendarEntries}
                    days={[]}
                    flaggedDays={flaggedDays}
                    loading={rosterLoading}
                    compact
                    emptyMessage=""
                    onEntryClick={(entry) =>
                      setSelected(
                        byId.get(entry.employee_request_id as number) ?? null
                      )
                    }
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>

      <RosterPeriodDrawer
        period={selected}
        onClose={() => setSelected(null)}
        onChanged={() => {
          setSelected(null)
          refetch()
        }}
      />
      {openCycleModal}

      <CustomModal
        open={confirmClose}
        title={`Close leave planning for ${cycle.planning_year}?`}
        onCancel={() => setConfirmClose(false)}
        footer={
          <div className="w-full flex justify-end gap-3">
            <Button
              color="neutral"
              onClick={() => setConfirmClose(false)}
            >
              Keep it open
            </Button>
            <Button
              color="primary"
              disabled={closeCycle.isLoading}
              submitting={closeCycle.isLoading}
              onClick={() =>
                closeCycle.mutate(cycle.leave_planning_cycle_id, {
                  onSuccess: () => setConfirmClose(false),
                })
              }
            >
              Close planning
            </Button>
          </div>
        }
      >
        <p className="m-0 font-avenir text-sm text-neutral500">
          Employees can no longer add or change plans for {cycle.planning_year}.
          Plans already made stay on the roster and can still become leave
          requests.
        </p>
      </CustomModal>
    </SegmentWrapper>
  )
}

/**
 * Periods to timeline rows and bars.
 *
 * A period awaiting revision is drawn as focus rather than planned: it is the one thing on
 * the roster that needs the supervisor to do something, and it should be findable without
 * reading every bar. Coordinated periods stay hatched — agreed is still not booked.
 */
function buildRoster(
  periods: ILeavePlanningPeriod[],
  windowStart: string,
  windowEnd: string
) {
  const rows: LeaveTimelineRow[] = []
  const bars: LeaveTimelineBar[] = []
  const byId = new Map<number, ILeavePlanningPeriod>()
  const seen = new Set<number>()

  periods.forEach((period) => {
    const rowId = period.person_id
    if (rowId == null) return

    const startDate = toIso(period.first_day)
    const endDate = toIso(period.last_day)
    if (!startDate || !endDate) return

    // String comparison is safe and cheap on ISO dates, and avoids constructing two dayjs
    // objects per period on every pan of the window.
    if (endDate < windowStart || startDate > windowEnd) return

    if (!seen.has(rowId)) {
      seen.add(rowId)
      rows.push({
        id: rowId,
        name: period.full_name ?? "Employee",
        subtitle: period.department ?? undefined,
      })
    }

    byId.set(period.leave_planning_period_id, period)

    bars.push({
      id: period.leave_planning_period_id,
      rowId,
      startDate,
      endDate,
      kind:
        period.status_id === PLANNING_STATUS.REVISION_REQUESTED
          ? "focus"
          : "planned",
      label: `${period.leave_policy ?? "Leave"} — ${planStatusLabel(
        period.status_id,
        period.status
      )}${
        period.indicative_days
          ? ` · ${period.indicative_days} days indicative`
          : ""
      }`,
    })
  })

  return { rows, bars, byId }
}

/** dd/MM/yyyy from the API to ISO. Split, not parsed — see LeaveOverlapPanel. */
function toIso(value?: string | null): string | undefined {
  if (!value) return undefined
  const [day, month, year] = value.split("/")
  if (!day || !month || !year) return undefined
  const iso = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`
  return dayjs(iso, ISO).isValid() ? iso : undefined
}

export default LeavePlanningRoster
